import React, { useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

// Componentes base (siempre visibles, no lazy)
import { NavBar } from '../components/UI/organisms/navbar/NavBar';
import { Footer } from '../components/UI/organisms/footer/Footer';
import { WhatsAppFloat } from '../components/UI/organisms/WhatsAppFloat/WhatsAppFloat';
import { ScrollToTop } from '../components/UI/atoms/ScrollToTop/ScrollToTop';
import { ErrorBoundary } from './ErrorBoundary';
import { lazyElement } from './RouteLoader';
import PublicLoading from '../components/UI/atoms/PublicLoading/PublicLoading';

// Páginas públicas (lazy - se cargan solo cuando el usuario visita la ruta)
const Home = React.lazy(() => import('../components/page/home/Home').then(m => ({ default: m.Home })));
const Nosotros = React.lazy(() => import('../components/page/nosotros/Nosotros').then(m => ({ default: m.Nosotros })));
const Productos = React.lazy(() => import('../components/page/productos/Productos').then(m => ({ default: m.Productos })));
const Plataforma = React.lazy(() => import('../components/page/plataforma/Platafoma').then(m => ({ default: m.Plataforma })));
const Sigo = React.lazy(() => import('../components/page/sigo/Sigo').then(m => ({ default: m.Sigo })));
const Forms = React.lazy(() => import('../components/page/forms/Forms').then(m => ({ default: m.Forms })));
const Contacto = React.lazy(() => import('../components/page/contacto/Contacto').then(m => ({ default: m.Contacto })));
const Cotizacion = React.lazy(() => import('../components/page/cotizacion/Cotizacion').then(m => ({ default: m.Cotizacion })));
const ThankYou = React.lazy(() => import('../components/page/thankyou/ThankYou').then(m => ({ default: m.ThankYou })));
const Agendar = React.lazy(() => import('../components/page/agendar/Agendar').then(m => ({ default: m.Agendar })));
const Login = React.lazy(() => import('../components/page/login/Login').then(m => ({ default: m.Login })));
const ResetPassword = React.lazy(() => import('../components/page/auth/ResetPassword'));
const Politicas = React.lazy(() => import('../components/page/politicas/Politicas').then(m => ({ default: m.Politicas })));
const Tratamiento = React.lazy(() => import('../components/page/politicas/Tratamientos').then(m => ({ default: m.Tratamiento })));
const Terminos = React.lazy(() => import('../components/page/politicas/Terminos').then(m => ({ default: m.Terminos })));
const TerminosSiigo = React.lazy(() => import('../components/page/politicas/TerminosSiigo').then(m => ({ default: m.TerminosSiigo })));
const TerminosSiigoPOS = React.lazy(() => import('../components/page/politicas/TerminosSiigoPOS').then(m => ({ default: m.TerminosSiigoPOS })));
const NotFound = React.lazy(() => import('../components/page/notfound/NotFound').then(m => ({ default: m.NotFound })));
const Tinkko = React.lazy(() => import('../components/page/tinkko/Tinkko'));
const TinkkoForm = React.lazy(() => import('../components/page/tinkko/TinkkoForm'));
const TinkkoThankYou = React.lazy(() => import('../components/page/tinkko/TinkkoThankYou'));
const AlquilerComputer = React.lazy(() => import('../components/page/alquiler/AlquilerComputer').then(m => ({ default: m.AlquilerComputer })));
const PublicRoutePage = React.lazy(() => import('../components/page/publicroute/PublicRoutePage'));

// Blog público (S2-117). Las tres URLs son las que el sitemap dinámico ya publica.
const BlogHome = React.lazy(() => import('../components/page/blog/BlogHome'));
const BlogCategory = React.lazy(() => import('../components/page/blog/BlogCategory'));
const BlogArticle = React.lazy(() => import('../components/page/blog/BlogArticle'));

// Overlays
const Catalogue = React.lazy(() => import('../components/page/productos/Catalogue').then(m => ({ default: m.Catalogue })));
const PopupWindowInfo = React.lazy(() => import('../components/UI/organisms/Section_3/PopupWindowInfo').then(m => ({ default: m.PopupWindowInfo })));

const publicLazy = (element) => lazyElement(element, <PublicLoading />);

const PublicRouter = () => {
    const [isCatalogue, setIsCatalogue] = useState(false);
    const [isWindow, setIsWindow] = useState(false);
    const [idCatalog, setIdCatalog] = useState("");
    const [idInfo, setIdInfo] = useState("");
    const location = useLocation();

    const shouldHideNavBarAndFooter = () => {
        const hideForRoutes = [
            "/login",
            "/contact/demo",
            "/gallery",
            "/register/guia",
            "/test/diagnostic",
            "/peopleday",
            "/peopleday/reservation",
            "/quiz/rest",
            "/aliados/tinkko",
            "/aliados/tinkko/form",
            "/aliados/tinkko/thank-you",
            "/participantes/reserva-cowork",
            "/participantes/registro-evento",
            new RegExp("/reset-password?.+"),
            new RegExp("/peopleday/reserva/?.+"),
            new RegExp("/quiz/rest/.+"),
        ];
        return hideForRoutes.some(route =>
            route instanceof RegExp ? route.test(location.pathname) : route === location.pathname
        );
    };

    const hideNavBarAndFooter = shouldHideNavBarAndFooter();

    return (
        <ErrorBoundary>
            {isWindow && lazyElement(<PopupWindowInfo setIsWindow={setIsWindow} id={idInfo} />, null)}
            {isCatalogue && lazyElement(<Catalogue setIsCatalogue={setIsCatalogue} idCatalog={idCatalog} />, null)}
            {!hideNavBarAndFooter && <NavBar />}
            <ScrollToTop />
            <Routes>
                <Route path='/' element={publicLazy(<Home setIdInfo={setIdInfo} setIsWindow={setIsWindow} />)} />
                <Route path='/productos' element={publicLazy(<Productos setIsCatalogue={setIsCatalogue} setIdCatalog={setIdCatalog} />)} />
                <Route path='/nosotros' element={publicLazy(<Nosotros />)} />
                <Route path='/plataforma' element={publicLazy(<Plataforma />)} />
                <Route path='/politicas_condiciones' element={publicLazy(<Politicas />)} />
                <Route path='/tratamiento_de_datos_personales' element={publicLazy(<Tratamiento />)} />
                <Route path='/terminos_condiciones_shofi' element={publicLazy(<Terminos />)} />

                {/* Formulario de contacto */}
                <Route path='/contacto' element={publicLazy(<Contacto />)} />
                <Route path='/cotizacion' element={publicLazy(<Cotizacion />)} />
                <Route path='/subscripcion' element={publicLazy(<Contacto />)} />
                <Route path='/subscripcion/true' element={publicLazy(<Contacto />)} />

                {/* Formularios de aliados */}
                <Route path='/index.php/invitacion-2/' element={publicLazy(<Sigo setIsWindow={setIsWindow} setIdInfo={setIdInfo} />)} />
                <Route path='/aliados/siigo' element={publicLazy(<Sigo setIsWindow={setIsWindow} setIdInfo={setIdInfo} />)} />
                <Route path='/diagnostico/test' element={publicLazy(<Sigo setIsWindow={setIsWindow} setIdInfo={setIdInfo} />)} />
                <Route path='/aliados/sigo/terminos_condiciones' element={publicLazy(<TerminosSiigo />)} />
                <Route path='/aliados/sigo/terminos_condiciones_POS' element={publicLazy(<TerminosSiigoPOS />)} />
                <Route path='/aliados/siigoPos' element={publicLazy(<Sigo setIsWindow={setIsWindow} setIdInfo={setIdInfo} />)} />
                <Route path='/aliados/treli' element={publicLazy(<Forms setIsWindow={setIsWindow} setIdInfo={setIdInfo} />)} />
                <Route path='/aliados/tinkko' element={publicLazy(<Tinkko />)} />
                <Route path='/aliados/tinkko/form' element={publicLazy(<TinkkoForm />)} />
                <Route path='/aliados/tinkko/thank-you' element={publicLazy(<TinkkoThankYou />)} />

                {/* Legacy People Day -> landing de Tinkko. Se mantiene la URL viva
                    (venía indexada / compartida) redirigiendo al aliado actual. */}
                <Route path='/peopleday' element={<Navigate to='/aliados/tinkko' replace />} />
                <Route path='/peopleday/*' element={<Navigate to='/aliados/tinkko' replace />} />

                <Route path='/download/guide' element={publicLazy(<Forms setIsWindow={setIsWindow} setIdInfo={setIdInfo} />)} />
                <Route path='/alquiler-computadores' element={publicLazy(<AlquilerComputer />)} />

                {/* Agendamiento antes del gracias */}
                <Route path='/agendar/:formId' element={publicLazy(<Agendar />)} />

                {/* Página de agradecimiento */}
                <Route path='/gracias/:formId' element={publicLazy(<ThankYou />)} />

                <Route path='/login' element={publicLazy(<Login />)} />
                <Route path='/login/:assignment' element={publicLazy(<Login />)} />
                <Route path='/reset-password' element={publicLazy(<ResetPassword to={"/reset-password"} />)} />

                {/* Rutas públicas dinámicas (landing pages gestionadas por SA-001/SA-005).
                    Prefijo reservado /p/:slug — ANTES del catch-all. */}
                <Route path='/p/:slug' element={publicLazy(<PublicRoutePage />)} />

                {/* Blog público. ORDEN IMPORTANTE: /blog y /blog/categoria/:slug
                    ANTES de /blog/:slug, y las tres antes del catch-all. */}
                <Route path='/blog' element={publicLazy(<BlogHome />)} />
                <Route path='/blog/categoria/:slug' element={publicLazy(<BlogCategory />)} />
                <Route path='/blog/:slug' element={publicLazy(<BlogArticle />)} />

                {/* Catch-all 404 */}
                <Route path='*' element={publicLazy(<NotFound home="/" />)} />
            </Routes>
            {!hideNavBarAndFooter && <Footer />}
            {/* Botón flotante WhatsApp: aparece en todas las páginas públicas si
                el super-admin lo activó (el componente decide si renderiza). */}
            <WhatsAppFloat />
        </ErrorBoundary>
    );
};

export default PublicRouter;
